import React from 'react';
import { useNavigate } from 'react-router-dom';

import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ChecklistIcon from '@mui/icons-material/Checklist';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import PostAddIcon from '@mui/icons-material/PostAdd';
import SettingsIcon from '@mui/icons-material/Settings';
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt';
import {
  Box,
  Button,
  Grid,
  IconButton,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import { useAuth } from 'contexts/AuthContext';

import RequestStatusChip from 'components/chip/RequestStatusChip';
import RequestTypeChip from 'components/chip/RequestTypeChip';
import {
  EmptyHint,
  PagedWidgetList,
  StatBreakdownTile,
  StatTile,
  Widget,
  WidgetBarChart,
  WidgetRow,
} from 'components/dashboard/DashboardWidgets';
import DomainHero, { HERO_PALETTES } from 'components/dashboard/DomainHero';
import { ScreenKey } from 'constant/ScreenPermission';
import { useDashboardProfile } from 'pages/useDashboardProfile';
import {
  ContractRequestListResult,
  RequestStatus,
  listContractRequest,
} from 'stores/ContractRequestStoreAdapter';
import { ContractListResult, listContract } from 'stores/ContractStoreAdapter';
import { clampPage } from 'utils/UserProfileGuard';

const ACCENT = '#6a1b9a';

/**
 * 「担当中の申請」とみなす進行中（未承認）のステータス。
 * 承認(APPROVED)・否決(REJECTED)・取消(CANCELLED)の決着済みは含めない。
 */
const PENDING_REQUEST_STATUSES: RequestStatus[] = ['ADJUSTING', 'SUBMITTED', 'IN_PROGRESS'];

/**
 * 契約ドメインのトップ（ヒーロー + ウィジェットダッシュボード）。
 * 「自分の申請」を最優先に据えた自分ごとダッシュボード。
 * - 自分が担当する進行中の申請（最優先アクション）
 * - 承認待ち（対応中・BACK_OFFICE 向け）
 * - KPI（進行中申請 / 承認待ち / 契約 / 申請中の契約）
 * - 機能ナビ（新規申請 / 申請一覧 / 契約一覧 / チェックリスト / フォーム / ペースト）
 */
const ContractHome: React.FC = () => {
  const navigate = useNavigate();
  const { userSession } = useAuth();
  const { allowedScreens, userId } = userSession;

  // ID指定の詳細は必ず別タブで開く
  const openRequestInNewTab = (contractRequestId: string) => {
    window.open(`/contract/request/${contractRequestId}`, '_blank', 'noopener,noreferrer');
  };

  // 設定（テンプレート/チェックリスト/ペースト）へのショートカット（権限のある項目のみ）
  const [settingsAnchor, setSettingsAnchor] = React.useState<HTMLElement | null>(null);
  const settingsItems = [
    {
      key: ScreenKey.CONTRACT_CHECKLIST,
      label: 'チェックリスト設定',
      icon: <ChecklistIcon fontSize="small" />,
      to: '/contract/checklist',
    },
    {
      key: ScreenKey.CONTRACT_TEMPLATE,
      label: '申請フォーム設定',
      icon: <ViewQuiltIcon fontSize="small" />,
      to: '/contract/template',
    },
    {
      key: ScreenKey.CONTRACT_PASTE,
      label: 'ペースト定義設定',
      icon: <ContentPasteIcon fontSize="small" />,
      to: '/contract/paste',
    },
  ].filter((it) => allowedScreens.includes(it.key));

  const [requests, setRequests] = React.useState<ContractRequestListResult[]>([]);
  const [contracts, setContracts] = React.useState<ContractListResult[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);

  // ダッシュボードのリストページ状態を user_profile(AUTO) で保存/復元する
  const [dashboard, setDashboard] = useDashboardProfile('contract-home-dashboard', {
    myRequestsPage: 0,
    myContractsPage: 0,
  });
  const setMyRequestsPage = React.useCallback(
    (p: number) => setDashboard((prev) => ({ ...prev, myRequestsPage: p })),
    [setDashboard],
  );
  const setMyContractsPage = React.useCallback(
    (p: number) => setDashboard((prev) => ({ ...prev, myContractsPage: p })),
    [setDashboard],
  );

  // ウィジェットの元データ（申請・契約）を一括取得する。初回ロードと更新ボタンで共用。
  const reload = React.useCallback(async () => {
    try {
      const [r, c] = await Promise.all([
        listContractRequest({}).catch((): ContractRequestListResult[] => []),
        listContract({}).catch((): ContractListResult[] => []),
      ]);
      setRequests(r);
      setContracts(c);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    reload();
  }, [reload]);

  // ヒーローの更新ボタン: 再取得中はスピナー表示にする
  const handleRefresh = React.useCallback(async () => {
    setRefreshing(true);
    try {
      await reload();
    } finally {
      setRefreshing(false);
    }
  }, [reload]);

  // 申請種別ごとの「申請中(SUBMITTED)」「対応中(IN_PROGRESS)」件数（全体）
  const countByType = React.useCallback(
    (type: 'CREATE' | 'CHANGE' | 'TERMINATE', status: 'SUBMITTED' | 'IN_PROGRESS') =>
      requests.filter((r) => r.requestType === type && r.requestStatus === status).length,
    [requests],
  );

  // 自分が担当の申請（担当者=自分、かつ未承認＝進行中のもの）。
  // 承認(APPROVED)・否決(REJECTED)・取消(CANCELLED)の決着済みは除外し、
  // 調整中(ADJUSTING)・申請中(SUBMITTED)・対応中(IN_PROGRESS)のみを「担当中」とする。
  const myRequests = React.useMemo(
    () =>
      requests.filter(
        (r) => r.personInChargeId === userId && PENDING_REQUEST_STATUSES.includes(r.requestStatus),
      ),
    [requests, userId],
  );

  const activeContracts = React.useMemo(
    () => contracts.filter((c) => c.status === 'ACTIVE'),
    [contracts],
  );
  // 自分が担当の契約（契約中のみ）
  const myActiveContracts = React.useMemo(
    () => activeContracts.filter((c) => c.personInChargeId === userId),
    [activeContracts, userId],
  );

  // 稼働中契約を元にした集計（可視化用）。要員数 = 重複しない memberId 数。
  // ラベル→ID の対応も保持し、バークリック時に契約一覧を id で絞り込む。
  const membersByProject = React.useMemo(() => {
    const map = new Map<string, { name: string; members: Set<string> }>();
    activeContracts.forEach((c) => {
      if (!c.projectId) return;
      const entry = map.get(c.projectId) ?? {
        name: c.projectName || c.projectId,
        members: new Set(),
      };
      if (c.memberId) entry.members.add(c.memberId);
      map.set(c.projectId, entry);
    });
    const data = Array.from(map.entries()).map(([id, v]) => ({
      label: v.name,
      value: v.members.size,
      id,
    }));
    return data;
  }, [activeContracts]);

  const membersByManager = React.useMemo(() => {
    const map = new Map<string, { name: string; members: Set<string> }>();
    activeContracts.forEach((c) => {
      if (!c.managerId) return;
      const entry = map.get(c.managerId) ?? {
        name: c.managerName || c.managerId,
        members: new Set(),
      };
      if (c.memberId) entry.members.add(c.memberId);
      map.set(c.managerId, entry);
    });
    return Array.from(map.entries()).map(([id, v]) => ({
      label: v.name,
      value: v.members.size,
      id,
    }));
  }, [activeContracts]);

  // 契約形態別の稼働要員数（重複しない memberId 数）。バークリックで契約形態で絞り込む。
  const membersByContractType = React.useMemo(() => {
    const map = new Map<string, { name: string; members: Set<string> }>();
    activeContracts.forEach((c) => {
      if (!c.contractTypeId) return;
      const entry = map.get(c.contractTypeId) ?? {
        name: c.contractTypeName || c.contractTypeId,
        members: new Set<string>(),
      };
      if (c.memberId) entry.members.add(c.memberId);
      map.set(c.contractTypeId, entry);
    });
    return Array.from(map.entries()).map(([id, v]) => ({
      label: v.name,
      value: v.members.size,
      id,
    }));
  }, [activeContracts]);

  // ラベル → ID 逆引き（バークリックで契約一覧を絞り込む）
  const projectIdByLabel = (label: string) => membersByProject.find((d) => d.label === label)?.id;
  const managerIdByLabel = (label: string) => membersByManager.find((d) => d.label === label)?.id;
  const contractTypeIdByLabel = (label: string) =>
    membersByContractType.find((d) => d.label === label)?.id;

  return (
    <Box sx={{ height: '100%', overflow: 'auto', pr: 1 }}>
      <Stack spacing={2.5} sx={{ maxWidth: 1100 }}>
        {/* ヒーロー */}
        <DomainHero
          eyebrow="Contract Management"
          title="Requests & Contracts, In Flow"
          tagline="Track the requests you own, review what's waiting for approval, and manage every contract through its lifecycle."
          palette={HERO_PALETTES.contract}
          onBack={() => navigate('/')}
          onRefresh={handleRefresh}
          refreshing={refreshing}
          actions={
            <Stack direction="row" spacing={1} alignItems="center">
              {settingsItems.length > 0 && (
                <>
                  <Tooltip title="設定">
                    <IconButton
                      onClick={(e) => setSettingsAnchor(e.currentTarget)}
                      sx={{
                        color: 'rgba(255,255,255,0.9)',
                        border: 1,
                        borderColor: 'rgba(255,255,255,0.35)',
                        borderRadius: 2,
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' },
                      }}
                    >
                      <SettingsIcon />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={settingsAnchor}
                    open={Boolean(settingsAnchor)}
                    onClose={() => setSettingsAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    slotProps={{
                      paper: {
                        elevation: 0,
                        sx: {
                          mt: 0.5,
                          minWidth: 220,
                          borderRadius: 2,
                          border: 1,
                          borderColor: 'divider',
                        },
                      },
                    }}
                  >
                    {settingsItems.map((it) => (
                      <MenuItem
                        key={it.key}
                        onClick={() => {
                          setSettingsAnchor(null);
                          navigate(it.to);
                        }}
                      >
                        <ListItemIcon sx={{ color: 'text.secondary' }}>{it.icon}</ListItemIcon>
                        <ListItemText primaryTypographyProps={{ variant: 'body2' }}>
                          {it.label}
                        </ListItemText>
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}
              {allowedScreens.includes(ScreenKey.CONTRACT_REQUEST_CREATE) && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/contract/contract-request-create')}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: 2,
                    boxShadow: 'none',
                    bgcolor: '#fff',
                    color: ACCENT,
                    '& .MuiButton-startIcon': { color: ACCENT },
                    '&:hover': { boxShadow: 'none', bgcolor: '#f3e6fa' },
                  }}
                >
                  新規申請
                </Button>
              )}
            </Stack>
          }
        />

        {/* KPI ストリップ（最上部）: 申請種別ごとの申請中/対応中 内訳 ＋ 契約数 */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 6, md: 3 }}>
            <StatBreakdownTile
              icon={<PostAddIcon sx={{ fontSize: 16 }} />}
              label="新規申請"
              total={countByType('CREATE', 'SUBMITTED') + countByType('CREATE', 'IN_PROGRESS')}
              color="#2e7d32"
              loading={loading}
              breakdown={[
                {
                  label: '申請中',
                  value: countByType('CREATE', 'SUBMITTED'),
                  onClick: () =>
                    navigate('/contract/request?requestType=CREATE&requestStatus=SUBMITTED'),
                },
                {
                  label: '対応中',
                  value: countByType('CREATE', 'IN_PROGRESS'),
                  onClick: () =>
                    navigate('/contract/request?requestType=CREATE&requestStatus=IN_PROGRESS'),
                },
              ]}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <StatBreakdownTile
              icon={<AutorenewIcon sx={{ fontSize: 16 }} />}
              label="変更申請"
              total={countByType('CHANGE', 'SUBMITTED') + countByType('CHANGE', 'IN_PROGRESS')}
              color="#1565c0"
              loading={loading}
              breakdown={[
                {
                  label: '申請中',
                  value: countByType('CHANGE', 'SUBMITTED'),
                  onClick: () =>
                    navigate('/contract/request?requestType=CHANGE&requestStatus=SUBMITTED'),
                },
                {
                  label: '対応中',
                  value: countByType('CHANGE', 'IN_PROGRESS'),
                  onClick: () =>
                    navigate('/contract/request?requestType=CHANGE&requestStatus=IN_PROGRESS'),
                },
              ]}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <StatBreakdownTile
              icon={<EventBusyIcon sx={{ fontSize: 16 }} />}
              label="解除申請"
              total={
                countByType('TERMINATE', 'SUBMITTED') + countByType('TERMINATE', 'IN_PROGRESS')
              }
              color="#c62828"
              loading={loading}
              breakdown={[
                {
                  label: '申請中',
                  value: countByType('TERMINATE', 'SUBMITTED'),
                  onClick: () =>
                    navigate('/contract/request?requestType=TERMINATE&requestStatus=SUBMITTED'),
                },
                {
                  label: '対応中',
                  value: countByType('TERMINATE', 'IN_PROGRESS'),
                  onClick: () =>
                    navigate('/contract/request?requestType=TERMINATE&requestStatus=IN_PROGRESS'),
                },
              ]}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <StatTile
              icon={<DescriptionOutlinedIcon sx={{ fontSize: 16 }} />}
              label="契約数（契約中）"
              value={activeContracts.length}
              suffix="件"
              color={ACCENT}
              loading={loading}
              onClick={() => navigate('/contract/contract-list')}
            />
          </Grid>
        </Grid>

        {/* 自分の申請 / 自分の契約（2カラム） */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Widget
              title={`申請中のチケット${loading ? '' : `（${myRequests.length}）`}`}
              action={
                <Link
                  component="button"
                  variant="caption"
                  underline="hover"
                  onClick={() => navigate('/contract/request?personInChargeId=self')}
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
                >
                  申請一覧
                  <ArrowForwardIcon sx={{ fontSize: 14 }} />
                </Link>
              }
            >
              {loading ? (
                <Stack spacing={1}>
                  {[0, 1, 2].map((i) => (
                    <Skeleton key={i} variant="rounded" height={44} />
                  ))}
                </Stack>
              ) : myRequests.length === 0 ? (
                <EmptyHint text="担当している申請はありません" />
              ) : (
                <PagedWidgetList
                  items={myRequests}
                  keyOf={(r) => r.contractRequestId}
                  page={
                    clampPage(dashboard.myRequestsPage + 1, Math.ceil(myRequests.length / 6)) - 1
                  }
                  onPageChange={setMyRequestsPage}
                  renderItem={(r) => (
                    <WidgetRow
                      accentColor={ACCENT}
                      onClick={() => openRequestInNewTab(r.contractRequestId)}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <Typography variant="caption" color="text.secondary" fontWeight={700}>
                            {r.managementId}
                          </Typography>
                          <RequestTypeChip requestType={r.requestType} />
                        </Stack>
                        <Typography variant="body2" noWrap>
                          {r.projectName}・{r.memberName}
                        </Typography>
                      </Box>
                      <RequestStatusChip requestStatus={r.requestStatus} />
                    </WidgetRow>
                  )}
                />
              )}
            </Widget>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Widget
              title={`担当中の契約${loading ? '' : `（${myActiveContracts.length}）`}`}
              action={
                <Link
                  component="button"
                  variant="caption"
                  underline="hover"
                  onClick={() => navigate('/contract/contract-list?personInChargeId=self')}
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
                >
                  契約一覧
                  <ArrowForwardIcon sx={{ fontSize: 14 }} />
                </Link>
              }
            >
              {loading ? (
                <Stack spacing={1}>
                  {[0, 1, 2].map((i) => (
                    <Skeleton key={i} variant="rounded" height={44} />
                  ))}
                </Stack>
              ) : myActiveContracts.length === 0 ? (
                <EmptyHint text="担当している契約中の契約はありません" />
              ) : (
                <PagedWidgetList
                  items={myActiveContracts}
                  keyOf={(c) => c.contractId}
                  page={
                    clampPage(
                      dashboard.myContractsPage + 1,
                      Math.ceil(myActiveContracts.length / 6),
                    ) - 1
                  }
                  onPageChange={setMyContractsPage}
                  renderItem={(c) => (
                    <WidgetRow
                      accentColor={ACCENT}
                      onClick={() =>
                        navigate(
                          `/contract/contract-list?managementId=${encodeURIComponent(c.managementId)}`,
                        )
                      }
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={700}>
                          {c.managementId}
                        </Typography>
                        <Typography variant="body2" noWrap>
                          {c.projectName}・{c.memberName}
                        </Typography>
                      </Box>
                    </WidgetRow>
                  )}
                />
              )}
            </Widget>
          </Grid>
        </Grid>

        {/* 稼働状況の可視化（稼働中契約ベース） */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Widget
              fit="content"
              title="プロジェクト別の稼働要員数"
              action={
                <Link
                  component="button"
                  variant="caption"
                  underline="hover"
                  onClick={() => navigate('/contract/contract-list')}
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
                >
                  契約一覧
                  <ArrowForwardIcon sx={{ fontSize: 14 }} />
                </Link>
              }
            >
              {loading ? (
                <Skeleton variant="rounded" height={200} />
              ) : (
                <WidgetBarChart
                  data={membersByProject}
                  color={ACCENT}
                  emptyText="稼働中の契約がありません"
                  onBarClick={(label) => {
                    const id = projectIdByLabel(label);
                    if (id) navigate(`/contract/contract-list?projectId=${id}`);
                  }}
                />
              )}
            </Widget>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Widget
              fit="content"
              title="課長別の稼働要員数"
              action={
                <Link
                  component="button"
                  variant="caption"
                  underline="hover"
                  onClick={() => navigate('/contract/contract-list')}
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
                >
                  契約一覧
                  <ArrowForwardIcon sx={{ fontSize: 14 }} />
                </Link>
              }
            >
              {loading ? (
                <Skeleton variant="rounded" height={200} />
              ) : (
                <WidgetBarChart
                  data={membersByManager}
                  color="#1565c0"
                  emptyText="稼働中の契約がありません"
                  onBarClick={(label) => {
                    const id = managerIdByLabel(label);
                    if (id) navigate(`/contract/contract-list?managerId=${id}`);
                  }}
                />
              )}
            </Widget>
          </Grid>
        </Grid>

        {/* 契約形態別の稼働要員数 */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Widget
              fit="content"
              title="契約形態別の稼働要員数"
              action={
                <Link
                  component="button"
                  variant="caption"
                  underline="hover"
                  onClick={() => navigate('/contract/contract-list')}
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
                >
                  契約一覧
                  <ArrowForwardIcon sx={{ fontSize: 14 }} />
                </Link>
              }
            >
              {loading ? (
                <Skeleton variant="rounded" height={200} />
              ) : (
                <WidgetBarChart
                  data={membersByContractType}
                  color="#00838f"
                  emptyText="稼働中の契約がありません"
                  onBarClick={(label) => {
                    const id = contractTypeIdByLabel(label);
                    if (id) navigate(`/contract/contract-list?contractTypeId=${id}`);
                  }}
                />
              )}
            </Widget>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};

export default ContractHome;
